import type { RefreshTokenStore } from '@runnerboy/api-client';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Preferences } from '@capacitor/preferences';

/** True inside the Runner Boy consumer Android app (Capacitor), false in the browser/PWA. */
export const isNative = Capacitor.isNativePlatform();

const SERVER_KEY = 'rb.server';
const REFRESH_KEY = 'rb.rt';

/** Build-time API origin for the native app. Demo builds may override it at runtime (Account → Server). */
export const DEFAULT_SERVER = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:4100';
export const SERVER_OVERRIDE_ALLOWED = import.meta.env.VITE_CHANNEL !== 'production';

const isLoopback = (u: string) => /^https?:\/\/(localhost|127\.0\.0\.1|10\.0\.2\.2)(:|\/|$)/i.test(u);

/** API origin: '' (same-origin) on the web; absolute URL in the native app. */
export function apiOrigin(): string {
  if (!isNative) return '';
  if (SERVER_OVERRIDE_ALLOWED) {
    try {
      const v = localStorage.getItem(SERVER_KEY);
      // A loopback override saved while testing over USB must not pin an HTTPS build to the developer PC.
      if (v && !(isLoopback(v) && DEFAULT_SERVER.startsWith('https://'))) return v;
    } catch {
      /* storage unavailable */
    }
  }
  return DEFAULT_SERVER;
}

export function setServerOverride(url: string | null) {
  try {
    if (url) localStorage.setItem(SERVER_KEY, url.replace(/\/$/, ''));
    else localStorage.removeItem(SERVER_KEY);
  } catch {
    /* ignore */
  }
}

/** Refresh token in app-private storage (Android SharedPreferences) — never in a cookie or localStorage. */
export const nativeRefreshStore: RefreshTokenStore = {
  async get() {
    return (await Preferences.get({ key: REFRESH_KEY })).value;
  },
  async set(token) {
    if (token) await Preferences.set({ key: REFRESH_KEY, value: token });
    else await Preferences.remove({ key: REFRESH_KEY });
  },
};

/** One-shot device position: native permission flow in the app, browser geolocation on the web. */
export async function currentPosition(): Promise<{ lat: number; lng: number; accuracy: number }> {
  if (isNative) {
    const perm = await Geolocation.checkPermissions();
    if (perm.location !== 'granted') {
      const req = await Geolocation.requestPermissions({ permissions: ['location'] });
      if (req.location !== 'granted' && req.coarseLocation !== 'granted') throw new Error('PERMISSION_DENIED');
    }
    const p = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 });
    return { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
  }
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('UNSUPPORTED'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (e) => reject(new Error(e.code === e.PERMISSION_DENIED ? 'PERMISSION_DENIED' : 'UNAVAILABLE')),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
  });
}

/** Native chrome setup: status bar colour, splash hide, Android back button → history back. */
export async function initNativeShell(onBack: () => boolean) {
  if (!isNative) return;
  document.documentElement.classList.add('rb-native');
  const [{ StatusBar, Style }, { SplashScreen }, { App }] = await Promise.all([import('@capacitor/status-bar'), import('@capacitor/splash-screen'), import('@capacitor/app')]);
  try {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#faf8f4' });
  } catch {
    /* not supported on this device */
  }
  void App.addListener('backButton', ({ canGoBack }) => {
    if (onBack()) return;
    if (canGoBack) window.history.back();
    else void App.minimizeApp();
  });
  setTimeout(() => void SplashScreen.hide(), 150);
}
